import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/tracker";
import { FadeUp, Overline } from "@/components/Reveal";

const STATUS_STYLES = {
  AVAILABLE: { dot: "#10B981", pill: "bg-emerald-50 text-emerald-700" },
  ALLOCATION: { dot: "#F59E0B", pill: "bg-amber-50 text-amber-700" },
  SOURCING: { dot: "#0A192F", pill: "bg-[#0A192F]/[0.06] text-midnight" },
  PROJECT: { dot: "#6366F1", pill: "bg-indigo-50 text-indigo-600" },
  ENQUIRE: { dot: "#6B7280", pill: "bg-gray-100 text-gray-600" },
};

const TABS = ["ALL", "GPU & AI", "SERVERS", "CPU", "MEMORY", "STORAGE", "NETWORKING", "COMPONENTS", "CONSUMER TECH"];

const CATEGORY_GROUPS = {
  "Enterprise GPU": "GPU & AI",
  "Data Centre GPU": "GPU & AI",
  "GPU Server": "GPU & AI",
  "AI Workstation": "GPU & AI",
  "Server CPU": "CPU",
  "Server Memory": "MEMORY",
  "Enterprise SSD": "STORAGE",
  "Components": "COMPONENTS",
};

const groupOf = (item) => item.group || CATEGORY_GROUPS[item.category] || "COMPONENTS";

export const AvailabilityBoard = () => {
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("ALL");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/availability").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, []);

  const filtered = items ? (tab === "ALL" ? items : items.filter((i) => groupOf(i) === tab)) : [];

  return (
    <section id="availability" className="relative py-16 lg:py-24 bg-white" data-testid="availability-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <FadeUp>
          <Overline>Availability</Overline>
          <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-midnight">
            Technology availability
          </h2>
          <p className="text-midnight/55 text-lg mt-5 max-w-2xl leading-relaxed">
            Representative product classes currently handled across our supply channels.
          </p>
        </FadeUp>

        <FadeUp delay={0.05} className="mt-10">
          <div className="flex flex-wrap gap-2" data-testid="board-tabs">
            {TABS.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`board-tab-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`text-[11px] font-bold tracking-[0.1em] px-4 py-2 transition-colors ${
                  tab === t
                    ? "bg-midnight text-white"
                    : "bg-white text-midnight/55 hairline hover:border-gold hover:text-gold"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </FadeUp>

        <FadeUp delay={0.1} className="mt-6">
          <div className="bg-white hairline shadow-soft" data-testid="availability-board">
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-midnight/10 text-xs font-bold uppercase tracking-[0.15em] text-midnight/40">
              <span className="col-span-2">Category</span>
              <span className="col-span-4">Specification</span>
              <span className="col-span-2">Region</span>
              <span className="col-span-2">Status</span>
              <span className="col-span-2 text-right">Action</span>
            </div>

            {items === null ? (
              <div className="px-6 py-10 text-sm text-midnight/40 animate-pulse">Loading availability…</div>
            ) : filtered.length === 0 ? (
              <div className="px-6 py-10 text-sm text-midnight/50" data-testid="board-empty">
                No public entries in this category yet — availability is provided against qualified enquiry.
              </div>
            ) : (
              filtered.map((item, i) => {
                const st = STATUS_STYLES[item.status] || STATUS_STYLES.ENQUIRE;
                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.04 }}
                    data-testid={`board-row-${item.category.toLowerCase().replace(/\s+/g, "-")}`}
                    className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 items-center px-6 py-5 border-b border-midnight/[0.07] hover:bg-[#FAFAFA] transition-colors"
                  >
                    <span className="md:col-span-2 font-heading font-medium text-midnight">{item.category}</span>
                    <span className="md:col-span-4 text-sm text-midnight/60">
                      {item.description}
                      {item.spec_class && <span className="block text-xs text-midnight/40 mt-0.5">{item.spec_class}</span>}
                    </span>
                    <span className="md:col-span-2 text-sm text-midnight/50">{item.region || "—"}</span>
                    <span className="md:col-span-2">
                      <span className={`inline-flex items-center gap-2 px-3 py-1.5 text-[11px] font-bold tracking-[0.1em] ${st.pill}`}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: st.dot }} />
                        {item.status}
                      </span>
                    </span>
                    <span className="md:col-span-2 md:text-right">
                      <button
                        onClick={() => {
                          trackEvent("availability_requested", { category: item.category });
                          navigate(`/request?category=${encodeURIComponent(item.category)}&product=${encodeURIComponent(item.description)}`);
                        }}
                        data-testid={`board-request-${item.category.toLowerCase().replace(/\s+/g, "-")}`}
                        className="text-xs font-bold tracking-[0.1em] text-midnight border border-midnight/20 px-4 py-2 hover:border-gold hover:text-gold transition-colors"
                      >
                        REQUEST →
                      </button>
                    </span>
                  </motion.div>
                );
              })
            )}
          </div>
          <p className="mt-5 text-xs text-midnight/40 leading-relaxed" data-testid="board-disclaimer">
            Demonstration entries. Manufacturer-specific availability, quantities and pricing are provided directly against qualified enquiries.
          </p>
        </FadeUp>
      </div>
    </section>
  );
};
